import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Circle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, Button } from '../components';
import { authAPI } from '../services/api';

/** Zelfde eisen als ValidationUtils.isValidPassword op de server. */
const EISEN: Array<{ key: string; label: string; test: (w: string) => boolean }> = [
  { key: 'lengte', label: 'Minimaal 8 tekens', test: (w) => w.length >= 8 },
  { key: 'hoofdletter', label: 'Een hoofdletter', test: (w) => /[A-Z]/.test(w) },
  { key: 'kleineLetter', label: 'Een kleine letter', test: (w) => /[a-z]/.test(w) },
  { key: 'cijfer', label: 'Een cijfer', test: (w) => /\d/.test(w) },
  { key: 'speciaal', label: 'Een speciaal teken (!@#$%^&* enz.)', test: (w) => /[!@#$%^&*(),.?":{}|<>]/.test(w) },
];

/** Nieuw wachtwoord instellen via de link uit de reset-mail (/reset-password?token=…). */
export const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const token = useSearchParams()[0].get('token');
  const [wachtwoord, setWachtwoord] = useState('');
  const [herhaling, setHerhaling] = useState('');
  const [bezig, setBezig] = useState(false);
  const [fouten, setFouten] = useState<string[]>([]);

  const voldoet = EISEN.every((e) => e.test(wachtwoord));
  const gelijk = wachtwoord.length > 0 && wachtwoord === herhaling;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !voldoet || !gelijk) return;
    setBezig(true);
    setFouten([]);
    try {
      await authAPI.resetPassword(token, wachtwoord);
      toast.success(t('wachtwoordReset.gelukt', 'Je wachtwoord is gewijzigd. Log in met je nieuwe wachtwoord.'));
      navigate('/login', { replace: true });
    } catch (err: any) {
      const data = err.response?.data;
      setFouten(data?.errors?.length ? data.errors : [data?.message || t('wachtwoordReset.resetFout', 'Wijzigen mislukt. Vraag een nieuwe link aan.')]);
    } finally {
      setBezig(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="max-w-md w-full">
        <Card>
          <div className="p-6">
            {!token ? (
              <div className="text-center">
                <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
                <h1 className="text-xl font-bold text-gray-900 mb-2">{t('wachtwoordReset.geenTokenTitel', 'Ongeldige link')}</h1>
                <p className="text-gray-600 mb-6">{t('wachtwoordReset.geenTokenTekst', 'Deze link bevat geen resetcode. Vraag een nieuwe link aan.')}</p>
                <Link to="/forgot-password" className="text-blue-600 hover:underline">{t('wachtwoordReset.nieuweLink', 'Nieuwe link aanvragen')}</Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <h1 className="text-xl font-bold text-gray-900 text-center">{t('wachtwoordReset.nieuwTitel', 'Nieuw wachtwoord instellen')}</h1>
                <div>
                  <label htmlFor="nieuw-wachtwoord" className="block text-sm font-medium text-gray-700 mb-1">{t('wachtwoordReset.nieuwLabel', 'Nieuw wachtwoord')}</label>
                  <input
                    id="nieuw-wachtwoord"
                    type="password"
                    required
                    autoComplete="new-password"
                    value={wachtwoord}
                    onChange={(e) => setWachtwoord(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <ul className="space-y-1 text-xs">
                  {EISEN.map((eis) => {
                    const ok = eis.test(wachtwoord);
                    return (
                      <li key={eis.key} className={`flex items-center gap-1.5 ${ok ? 'text-green-700' : 'text-gray-500'}`}>
                        {ok ? <CheckCircle className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                        {t(`wachtwoordReset.eis.${eis.key}`, eis.label)}
                      </li>
                    );
                  })}
                </ul>
                <div>
                  <label htmlFor="herhaal-wachtwoord" className="block text-sm font-medium text-gray-700 mb-1">{t('wachtwoordReset.herhaalLabel', 'Herhaal wachtwoord')}</label>
                  <input
                    id="herhaal-wachtwoord"
                    type="password"
                    required
                    autoComplete="new-password"
                    value={herhaling}
                    onChange={(e) => setHerhaling(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                  {herhaling.length > 0 && !gelijk && (
                    <p className="text-xs text-red-600 mt-1">{t('wachtwoordReset.nietGelijk', 'De wachtwoorden zijn niet gelijk.')}</p>
                  )}
                </div>
                {fouten.length > 0 && (
                  <div className="text-sm text-red-600 space-y-1">
                    {fouten.map((f) => <p key={f}>{f}</p>)}
                    <Link to="/forgot-password" className="text-blue-600 hover:underline">{t('wachtwoordReset.nieuweLink', 'Nieuwe link aanvragen')}</Link>
                  </div>
                )}
                <Button type="submit" loading={bezig} disabled={!voldoet || !gelijk} className="w-full">
                  {t('wachtwoordReset.opslaan', 'Wachtwoord opslaan')}
                </Button>
              </form>
            )}
            <p className="text-sm text-gray-500 mt-6 text-center">
              <Link to="/login" className="text-blue-600 hover:underline">{t('wachtwoordReset.terugNaarLogin', 'Terug naar inloggen')}</Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
